/**
 * PrivacyPolicyPage.tsx — /legal/privacy — ɳSentry SaaS Privacy Policy
 *
 * Purpose:    Privacy policy for the hosted service: what we collect, why,
 *             where it lives, sub-processors, retention per tier, cookies,
 *             and GDPR rights (with pointers to the built-in export/delete
 *             flows).
 * Inputs:     Static content
 * Outputs:    Legal document page
 * Constraints: Standard SaaS template, marked "review with counsel".
 * SOT:        nsentry SaaS completion spec § G-WEB (legal)
 */
import { Link } from 'react-router-dom'
import { LegalLayout } from './LegalLayout'

const SUB_PROCESSORS = [
  { name: 'Stripe', purpose: 'Subscription payments and invoicing', location: 'USA / EU' },
  { name: 'ElasticEmail', purpose: 'Transactional email (alerts, invites, receipts)', location: 'EU / USA' },
  { name: 'Hetzner Online', purpose: 'Application and database hosting', location: 'Germany (EU)' },
  { name: 'Cloudflare', purpose: 'DNS, TLS, and DDoS protection', location: 'Global edge' },
  { name: 'Vercel', purpose: 'Web application (SPA) hosting and CDN', location: 'Global edge' },
] as const

export function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="2026-07-03">
      <section>
        <h2 id="scope">1. Scope</h2>
        <p>
          This policy explains how the ɳSelf project (&quot;we&quot;) processes personal
          data when you use the hosted ɳSentry service at sentry.nself.org. It does not
          apply to self-hosted ɳSentry installations — those never send data to us.
        </p>
      </section>

      <section>
        <h2 id="data-we-collect">2. Data we collect</h2>
        <ul>
          <li>
            <strong>Account data:</strong> email address, display name, hashed password,
            team membership, and plan/billing tier.
          </li>
          <li>
            <strong>Monitoring data you send:</strong> monitor URLs and check results,
            error events and stack traces, RUM pageview metrics, heartbeat pings, incident
            records, and status-page content. You control what this contains — avoid
            sending personal data in error payloads where possible.
          </li>
          <li>
            <strong>Status-page subscribers:</strong> email addresses of people who opt in
            to incident notifications on your status pages. You are the controller for
            these; we process them on your behalf.
          </li>
          <li>
            <strong>Operational logs:</strong> IP addresses and request metadata, kept
            short-term for security and abuse prevention.
          </li>
          <li>
            <strong>Payments:</strong> handled by Stripe. We store your Stripe customer ID
            and subscription state, never your card number.
          </li>
        </ul>
        <p>We do not sell personal data, and we do not use advertising trackers.</p>
      </section>

      <section>
        <h2 id="purposes">3. Why we process it (legal bases)</h2>
        <ul>
          <li>Providing the Service you signed up for (contract, GDPR Art. 6(1)(b)).</li>
          <li>Billing and tax compliance (legal obligation, Art. 6(1)(c)).</li>
          <li>Security, abuse prevention, and service improvement (legitimate interest, Art. 6(1)(f)).</li>
          <li>Incident emails to status-page subscribers (consent, Art. 6(1)(a) — every email includes unsubscribe).</li>
        </ul>
      </section>

      <section>
        <h2 id="where">4. Where your data lives</h2>
        <p>
          Primary application data is stored on servers operated by Hetzner Online in
          Germany (EU). Web assets are delivered via Vercel and Cloudflare edge networks.
        </p>
      </section>

      <section>
        <h2 id="sub-processors">5. Sub-processors</h2>
        <p>We share data only with the vendors needed to run the Service:</p>
        <div className="mt-3 overflow-x-auto rounded-lg border border-brand-subtle">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-subtle bg-brand-surface text-left">
                <th scope="col" className="px-4 py-2.5 font-semibold text-brand-ink">Vendor</th>
                <th scope="col" className="px-4 py-2.5 font-semibold text-brand-ink">Purpose</th>
                <th scope="col" className="px-4 py-2.5 font-semibold text-brand-ink">Location</th>
              </tr>
            </thead>
            <tbody>
              {SUB_PROCESSORS.map((s, i) => (
                <tr key={s.name} className={i % 2 ? 'bg-brand-surface/40' : ''}>
                  <th scope="row" className="px-4 py-2.5 text-left font-medium text-gray-200">{s.name}</th>
                  <td className="px-4 py-2.5 text-brand-muted">{s.purpose}</td>
                  <td className="px-4 py-2.5 text-brand-muted">{s.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          Transfers outside the EU rely on Standard Contractual Clauses or an adequacy
          decision. We will update this list before adding a sub-processor.
        </p>
      </section>

      <section>
        <h2 id="retention">6. Retention</h2>
        <ul>
          <li>
            <strong>Monitoring data:</strong> retained per your plan&apos;s window (7 days
            on the free tier, 90 days on the ɳSentry Bundle, 365 days on ɳSelf+ — see{' '}
            <Link to="/pricing">pricing</Link>), then deleted or aggregated.
          </li>
          <li><strong>Account data:</strong> kept while your account exists; deleted within 30 days of account deletion.</li>
          <li><strong>Billing records:</strong> kept as long as tax/accounting law requires.</li>
          <li><strong>Operational logs:</strong> at most 90 days.</li>
        </ul>
      </section>

      <section>
        <h2 id="cookies">7. Cookies</h2>
        <p>
          The app uses a single strictly-necessary, httpOnly session cookie
          (<code>nsentry_auth_token</code>) to keep you signed in. No third-party or
          advertising cookies are set.
        </p>
      </section>

      <section>
        <h2 id="rights">8. Your rights</h2>
        <p>
          You can access, correct, export, restrict, object to the processing of, or
          delete your personal data. The export and deletion flows are built into the app
          (Settings → &quot;Export my data&quot; / &quot;Delete account&quot;) — see{' '}
          <Link to="/legal/gdpr">GDPR &amp; Data Processing</Link> for the step-by-step
          process. For anything else email{' '}
          <a href="mailto:privacy@nself.org">privacy@nself.org</a>. You may also lodge a
          complaint with your local supervisory authority.
        </p>
      </section>

      <section>
        <h2 id="changes">9. Changes</h2>
        <p>
          We will announce material changes to this policy by email or in-app notice
          before they take effect.
        </p>
      </section>
    </LegalLayout>
  )
}
